/* eslint-disable react/prop-types */
'use client';

import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useUser } from '@/contexts/UserContext';
import { createAddress } from '@/pages/vendor/api/addressApi.js';

// Shared Layout & UI Components
import Header from '@/components/layouts/Header/Header';
import BottomNav from '@/components/layouts/Footer/BottomNav';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

// Icons
import { Search, MapPin, User as UserIcon, Building, Loader2, User } from 'lucide-react';

export default function VendorAddress() {
  const [address, setAddress] = useState({
    addressLineOne: '',
    addressLineTwo: '',
    city: '',
    state: '',
    country: 'India',
    contactNo: '',
    pinCode: '',
    latitude: 0,
    longitude: 0,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const existingAddress = location?.state?.address;
  const { user } = useUser();

  const searchInputRef = useRef(null);

  useEffect(() => {
    if (existingAddress) {
      setAddress((prev) => ({ ...prev, ...existingAddress }));
      setSearchQuery();
    }
  }, [existingAddress]);

  // Initialize Google Maps Autocomplete
  useEffect(() => {
    if (window.google && searchInputRef.current) {
      const autocomplete = new window.google.maps.places.Autocomplete(searchInputRef.current, {
        componentRestrictions: { country: 'IN' },
        fields: ['address_components', 'formatted_address', 'geometry', 'name'],
      });
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();

        if (place.geometry) handlePlaceSelect(place);
      });
    }
  }, []);

  const handlePlaceSelect = (place) => {
    const location = place.geometry?.location;
    if (!location) return;
    updateSelectedLocation(place, location.lat(), location.lng());
  };

  const extractAddressComponents = (components, place = '') => {
    const get = (type) => components.find((c) => c.types.includes(type))?.long_name || '';
    return {
      addressLineOne: `${place?.name || ''}, ${get('route')}, ${get('neighborhood')}`.replace(
        /^, |, $/g,
        ''
      ),
      addressLineTwo: `${get('sublocality_level_2')}, ${get('sublocality_level_1')}`.replace(
        /^, |, $/g,
        ''
      ),
      city: get('locality') || get('administrative_area_level_3'),
      state: get('administrative_area_level_1'),
      pinCode: get('postal_code'),
      country: get('country'),
    };
  };

  const updateSelectedLocation = (place, lat, lng) => {
    const addressComponents = extractAddressComponents(place.address_components || [], place);
    setAddress((prev) => ({ ...prev, ...addressComponents, latitude: lat, longitude: lng }));
    setSearchQuery('');
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser.');
      return;
    }

    setIsDetectingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude: lat, longitude: lng } = position.coords;
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
          if (status === 'OK' && results?.[0]) {
            updateSelectedLocation(results[0], lat, lng);
          } else {
            toast.error('Could not determine address from your location.');
            setAddress((prev) => ({ ...prev, latitude: lat, longitude: lng }));
          }
          setIsDetectingLocation(false);
        });
      },
      (error) => {
        toast.error('Unable to get your current location.');
        console.error('Geolocation Error:', error);
        setIsDetectingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const handleInputChange = (field, value) => {
    setAddress((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    if (!address.addressLineOne || !address.city || !address.state || !address.pinCode) {
      toast.error('Please fill in all required address fields.');
      return false;
    }
    if (!address.latitude || !address.longitude) {
      toast.error('Please select a location using search or your current location.');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const apiData = { ...address, contactNo: user?.contactNo };
      console.log('Submitting Address:', apiData);
      const apiRes = await createAddress(apiData, user?.id);
      setAddress(apiRes);
      toast.success('Shop address saved successfully!');
      navigate(-1);
    } catch (error) {
      console.error('Failed to save address:', error);
      toast.error('Failed to save address. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-slate-50">
      <Header page="profile-address" />

      <main className="flex-grow mx-auto  px-4 py-4 pb-20 md:p-6 lg:px-6 lg:py-4">
        <div className="lg:grid lg:grid-cols-5 lg:gap-4 lg:items-start">
          {/* --- LEFT COLUMN: LOCATION FINDER --- */}
          <div className="lg:col-span-2 lg:sticky lg:top-20">
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200/80">
              <h2 className="text-xl font-bold pb-2 text-slate-900">Update Your Address</h2>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 h-5 w-5" />
                <Input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search address, landmark, area..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-12 text-base bg-slate-100"
                />
              </div>

              <div className="flex items-center my-4">
                <div className="flex-grow border-t border-slate-200"></div>
                <span className="flex-shrink mx-4 text-xs font-semibold text-slate-400">OR</span>
                <div className="flex-grow border-t border-slate-200"></div>
              </div>

              <Button
                onClick={getCurrentLocation}
                disabled={isDetectingLocation}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                <MapPin className="mr-2 h-4 w-4" />
                {isDetectingLocation ? 'Detecting Location...' : 'Use Current Location'}
              </Button>
            </div>
          </div>

          {/* --- RIGHT COLUMN: ADDRESS FORM --- */}
          <div className="lg:col-span-3 mt-8 lg:mt-0">
            <form
              onSubmit={handleSubmit}
              className="bg-white p-6 sm:p-8 rounded-2xl shadow-sm border border-slate-200/80 space-y-6"
            >
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Confirm Your Business Address</h1>
                <p className="text-slate-500 mt-1">Fields marked with * are required.</p>
              </div>

              <FormField
                id="addressLineOne"
                label="Address Line 1 *"
                value={address.addressLineOne}
                onChange={(e) => handleInputChange('addressLineOne', e.target.value)}
                placeholder="Street, Building, Market name"
                icon={Building}
              />
              <FormField
                id="addressLineTwo"
                label="Address Line 2 (Landmark)"
                value={address.addressLineTwo}
                onChange={(e) => handleInputChange('addressLineTwo', e.target.value)}
                placeholder="e.g., Near City Park"
                icon={User}
              />

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <FormField
                  id="city"
                  label="City *"
                  value={address.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  placeholder="City"
                  icon={UserIcon}
                />
                <FormField
                  id="state"
                  label="State *"
                  value={address.state}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                  placeholder="State"
                  icon={UserIcon}
                />
                <FormField
                  id="pinCode"
                  label="PIN Code *"
                  value={address.pinCode}
                  onChange={(e) => handleInputChange('pinCode', e.target.value)}
                  placeholder="PIN Code"
                  icon={UserIcon}
                />
              </div>

              <div className="pt-4 flex justify-end gap-3">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => navigate(-1)}
                  className="text-slate-600 hover:bg-slate-100"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700 font-semibold px-6"
                  disabled={isSubmitting}
                >
                  {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {existingAddress ? 'Update Address' : 'Save Address'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </main>

      <BottomNav />
    </div>
  );
}

// Reusable FormField sub-component for consistency
const FormField = ({ id, label, icon: Icon, helperText, ...props }) => (
  <div className="grid w-full items-center gap-2">
    <Label htmlFor={id} className="font-semibold text-slate-800">
      {label}
    </Label>
    <div className="relative flex items-center">
      <Icon className="absolute left-3 h-5 w-5 text-slate-400 pointer-events-none" />
      <Input
        id={id}
        className="pl-10 h-12 text-base bg-slate-50 border-none ring-1 outline-none ring-slate-200 focus-visible:ring-1 focus-visible:ring-offset-0 focus-visible:ring-green-700 focus:bg-white  disabled:bg-slate-100 disabled:cursor-not-allowed"
        {...props}
      />
    </div>
    {helperText && <p className="text-xs text-slate-500 mt-1 pl-1">{helperText}</p>}
  </div>
);
